import type {DiffSide, Thread} from '@github-ui/diff-lines/conversation-types'
import {getDiffLineMarkersKey} from '@github-ui/diff-lines/line-helpers'

// Right side = added line (R), left side = deleted line (L). Defaults to left to match
// the diff line key convention used elsewhere (e.g. getThreadPreviewsLineAndFileKeysFromThread).
function diffSideOf(side: DiffSide | null | undefined): 'LEFT' | 'RIGHT' {
  return side === 'RIGHT' ? 'RIGHT' : 'LEFT'
}

export interface ThreadFileLabel {
  /** Full file path, e.g. "packages/pull-requests/stores/slices/batch-comments.ts". */
  filePath: string
  /** Just the trailing file name, e.g. "batch-comments.ts". */
  fileName: string
  /** Human-readable line label, e.g. "Line R71" or "Lines R71 - R73". Empty when there is no line anchor. */
  lineText: string
  /** True when the comment targets the whole file rather than specific lines. */
  isFileLevel: boolean
  /** True when the thread is anchored to a file (has a path). */
  hasFile: boolean
  /** True when the thread is anchored to specific line(s). */
  hasLine: boolean
}

/**
 * Derives the file/line metadata for a comment thread, shared by the collapsed
 * header label (file name + lines) and the full file path row.
 */
export function getThreadFileLabel(thread: Thread): ThreadFileLabel {
  const filePath = thread.path ?? thread.positioning?.path ?? ''

  const isFileLevel = thread.subjectType?.toUpperCase() === 'FILE'
  const line = thread.positioning?.endLine ?? thread.subject?.endLine ?? thread.line
  const rawStartLine = thread.positioning?.startLine ?? thread.subject?.startLine ?? thread.startLine
  const startLine = rawStartLine && rawStartLine !== line ? rawStartLine : null

  const endSide = diffSideOf(thread.positioning?.endDiffSide ?? thread.subject?.endDiffSide ?? thread.diffSide)
  const startSide = diffSideOf(
    thread.positioning?.startDiffSide ?? thread.subject?.startDiffSide ?? thread.startDiffSide ?? endSide,
  )

  const hasLine = !isFileLevel && !!line
  const endLabel = getDiffLineMarkersKey(line ?? null, endSide)
  const lineText = !hasLine
    ? ''
    : startLine
      ? `Lines ${getDiffLineMarkersKey(startLine, startSide)} - ${endLabel}`
      : `Line ${endLabel}`

  const fileName = filePath ? (filePath.split('/').pop() ?? filePath) : ''

  return {filePath, fileName, lineText, isFileLevel, hasFile: !!filePath, hasLine}
}
